<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" />

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="d-flex justify-content-end">
          <McButton
            variant="secondary"
            class="me-2"
            icon="icon-fill_close"
            :text="$t('global.cancel')"
            @click="handleCancel"
          />

          <McButton
            v-if="isUpdate && !oldData?.forceInProgress"
            v-b-modal.mc-modal-change-to-service
            variant="secondary"
            class="me-2"
            icon="icon-outline_truck-maintenance"
            :text="$t('maintenance.changeToService')"
          />

          <McButton
            v-else-if="isUpdate && oldData?.forceInProgress"
            v-b-modal.mc-modal-change-to-service
            variant="primary"
            class="me-2"
            icon="icon-outline_truck-maintenance"
            :text="$t('maintenance.serviceToNormal')"
          />

          <McButton
            variant="primary"
            icon="icon-outline_save"
            :text="$t('global.save')"
            @click="submitData"
          />
        </div>
      </div>
    </div>
    <div class="clearfix mb-4">
      <h4 class="card-title float-start">
        {{ $t('master.form.fleet.fleetInformation') }}
      </h4>
    </div>
    <div>
      <div v-if="$route.params.vehicleId && isLoading">
        <b-card>
          <b-row>
            <b-col>
              <b-skeleton width="70%" height="50px" />
            </b-col>
            <b-col>
              <b-skeleton width="70%" height="50px" />
            </b-col>
            <b-col>
              <b-skeleton width="70%" height="50px" />
            </b-col>
            <b-col>
              <b-skeleton width="70%" height="50px" />
            </b-col>
            <b-col>
              <b-skeleton width="70%" height="50px" />
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col>
              <b-skeleton width="70%" height="50px" />
            </b-col>
          </b-row>
        </b-card>
      </div>
      <b-card v-else>
        <b-row class="mc-list-info__top">
          <b-col>
            <template v-if="$route.params.vehicleId">
              <span>{{ $t('global.object.licensePlate') }}</span>
              <strong>{{ vehicleSelected?.licensePlate }}</strong>
            </template>
            <FormulateInput
              v-else
              v-model="form.vehicleId"
              type="select-resourceful-v2"
              name="licensePlateSelect"
              :placeholder="$t('global.object.licensePlate')"
              :trackBy="'id'"
              :optionsGetter="resourcefulQuery.licensePlate"
              :optionLabelFn="({ item }) => item.licensePlate"
              :fullObject="vehicleSelected"
              validation="required:trim"
              error-behavior="submit"
              validation-behavior="submit"
              :multiple="false"
              tabIndex="0"
              @update:fullObject="($event) => (vehicleSelected = $event)"
            >
              <template #label>
                <div class="d-flex justify-content-between align-items-center">
                  <label>{{ $t('global.object.licensePlate') }} <i class="text-danger">*</i></label>
                </div>
              </template>
            </FormulateInput>
          </b-col>

          <b-col>
            <span>{{ $t('global.field.vehicleType') }}</span>
            <strong>{{
              vehicleSelected?.vehicleType
                ? vehicleSelected.vehicleType.type
                  ? vehicleSelected.vehicleType.type
                  : '-'
                : '-'
            }}</strong>
          </b-col>

          <b-col>
            <span>{{ $t('global.object.fleetCategory') }}</span>
            <strong>{{
              vehicleSelected?.vehicleType
                ? vehicleSelected.vehicleType.category
                  ? vehicleSelected.vehicleType.category
                  : '-'
                : '-'
            }}</strong>
          </b-col>

          <b-col>
            <span>{{ $t('setting.list.fleetSetting.fleetModel') }}</span>
            <strong>{{
              vehicleSelected?.vehicleType
                ? vehicleSelected.vehicleType.model
                  ? `${vehicleSelected.vehicleType.manufacturer} - ${vehicleSelected.vehicleType.model}`
                  : '-'
                : '-'
            }}</strong>
          </b-col>

          <b-col lg="2" md="6" class="border-start">
            <span>{{ $t('maintenance.field.odoFleetNow') }}</span>
            <strong>
              {{
                vehicleSelected
                  ? vehicleSelected.odometer
                    ? thousandFormat(vehicleSelected.odometer) + ' Km'
                    : '0'
                  : '0'
              }}
            </strong>
            <i
              v-if="$route.params.vehicleId"
              id="odoNow"
              class="mc-fa ms-2 icon-fill_sync"
              @click="refreshVehicle"
            ></i>
            <b-tooltip target="odoNow" triggers="hover" placement="bottom">
              <p>{{ $t('maintenance.tooltip.odoNow') }} :</p>
              <span>{{ lastPacket ? momentDMYHS(lastPacket) : '-' }}</span>
            </b-tooltip>
          </b-col>

          <b-col lg="2" md="5">
            <span>{{ $t('maintenance.form.hourmeterFleetNow') }}</span>
            <strong>
              {{
                vehicleSelected
                  ? vehicleSelected.hourmeter
                    ? minuteToHour(vehicleSelected.hourmeter)
                    : '-'
                  : '-'
              }}
            </strong>
            <i
              v-if="$route.params.vehicleId"
              id="hourmeterFleetNow"
              class="mc-fa ms-2 icon-fill_sync"
              @click="refreshVehicle"
            ></i>
            <b-tooltip target="hourmeterFleetNow" triggers="hover" placement="bottom">
              <p>{{ $t('maintenance.tooltip.odoNow') }} :</p>
              <span>{{ lastPacket ? momentDMYHS(lastPacket) : '-' }}</span>
            </b-tooltip>
          </b-col>
        </b-row>
        <b-row class="mt-3 mc-list-info__top">
          <b-col v-if="isMGMAndMO" lg="2" md="5">
            <span>{{ $t('maintenance.form.refrigeratorFleetNow') }}</span>
            <strong>
              {{
                vehicleSelected
                  ? vehicleSelected.refrigerator
                    ? minuteToHour(vehicleSelected.refrigerator)
                    : '-'
                  : '-'
              }}
            </strong>
            <i
              v-if="$route.params.vehicleId"
              id="refrigeratorFleetNow"
              class="mc-fa ms-2 icon-fill_sync"
              @click="refreshVehicle"
            ></i>
            <b-tooltip target="refrigeratorFleetNow" triggers="hover" placement="bottom">
              <p>{{ $t('maintenance.tooltip.odoNow') }} :</p>
              <span>{{ lastPacket ? momentDMYHS(lastPacket) : '-' }}</span>
            </b-tooltip>
          </b-col>
        </b-row>
      </b-card>
    </div>

    <div class="clearfix mb-4">
      <b-alert
        v-if="isUpdate && oldData?.forceInProgress"
        show
        variant="warning"
        class="mc-alert mt-3"
      >
        <i class="mc-fa icon-fill_info"></i>
        <span class="mc-alert__text">
          {{ $t('maintenance.notif.inService1') }}
          <strong>{{ vehicleSelected.licensePlate }}</strong>
          {{ $t('maintenance.notif.inService2') }}
          <strong>{{ `${servicesDatas?.name} (${servicesDatas?.serviceAction?.action})` }}</strong>
        </span>
      </b-alert>

      <h4 class="card-title float-start">
        {{ $t('maintenance.frontPage.title.infoCompService') }}
      </h4>
    </div>

    <div>
      <div v-if="isUpdate && !loaded">
        <b-card>
          <b-row class="my-3">
            <b-col>
              <b-skeleton width="100%" height="100px" />
            </b-col>
            <b-col>
              <b-row>
                <b-col md="3">
                  <b-skeleton width="70%" height="100px" />
                </b-col>
                <b-col md="3">
                  <b-skeleton width="70%" height="100px" />
                </b-col>
                <b-col md="3">
                  <b-skeleton width="70%" height="100px" />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row class="my-5">
            <b-col md="6">
              <b-skeleton width="70%" height="100px" />
            </b-col>
          </b-row>
          <b-row class="my-5">
            <b-col md="6">
              <b-skeleton width="70%" height="100px" />
            </b-col>
          </b-row>
          <b-row class="my-5">
            <b-col>
              <b-skeleton width="100%" height="50px" />
            </b-col>
            <b-col>
              <b-skeleton width="100%" height="50px" />
            </b-col>
          </b-row>
          <b-row class="my-5">
            <b-col>
              <b-skeleton width="100%" height="50px" />
            </b-col>
            <b-col>
              <b-skeleton width="100%" height="50px" />
            </b-col>
          </b-row>
        </b-card>
      </div>

      <b-card v-else>
        <FormulateForm ref="formRef" v-model="form">
          <b-row class="mb-3 pb-3">
            <b-col md="6">
              <FormulateInput
                v-model="compServiceSelect"
                type="value-help"
                :placeholder="`${$t('global.form.select')} ${$t('maintenance.form.compService')}`"
                :multiple="false"
                :listGetter="queryCompService.get"
                :label="$t('maintenance.form.compService')"
                :tableFields="fieldCompService"
                :trackBy="'serviceId'"
                :displayedBy="'name'"
                :initialObject="servicesDatas"
                :validation="['required']"
                error-behavior="submit"
                validation-behavior="submit"
                :visibleValidationErrors="true"
                :disabled="isUpdate || !vehicleSelected"
                :help="isUpdate || !vehicleSelected ? '' : 'required'"
              >
                <template #label>
                  <div class="d-flex justify-content-between align-items-center">
                    <label>{{ $t('maintenance.form.compService') }} </label>
                  </div>
                </template>
              </FormulateInput>
            </b-col>

            <b-col>
              <b-row>
                <b-col sm="4" xl="3">
                  <label>{{ $t('global.activity') }}</label>
                  <p class="mt-2">
                    {{ serviceAction }}
                  </p>
                </b-col>

                <b-col sm="4" xl="3">
                  <label>{{ $t('maintenance.form.catService') }}</label>
                  <p class="mt-2">
                    {{ categoryService }}
                  </p>
                </b-col>

                <b-col v-if="ssgroup.name" sm="4" xl="6">
                  <label>{{ $t('maintenance.frontPage.title.serviceGroup') }}</label>
                  <p class="mt-2">
                    {{ ssgroup.name }}
                  </p>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row md="12" class="py-3 my-3 metode-penjadwalan-options">
            <label>{{ $t('maintenance.methodScheduler') }}</label>
            <div class="d-flex flex-row">
              <b-col
                v-for="option in methodSchedulerOptions"
                :key="option.key"
                md="3"
                class="my-1 ps-0 ms-0"
              >
                <McRadioButton
                  :id="option.key"
                  :key="option.key"
                  v-model="methodScheduler"
                  v-b-tooltip.hover
                  name="method_scheduler"
                  :initialValue="option.key"
                  :label="`${option.label}`"
                  :disabled="isDisable"
                  :description="option.description"
                  :title="option.tooltip"
                  @click="handleSetMethod(option.key)"
                />
              </b-col>
            </div>
          </b-row>

          <b-row md="6" class="my-1 service-param-options">
            <label>{{ $t('maintenance.form.serviceParameter') }}</label>
            <div class="d-flex flex-row">
              <b-col
                v-for="option in parameterServiceOptions"
                :key="option.key"
                md="3"
                class="my-1"
              >
                <McRadioButton
                  :id="option.key"
                  :key="option.key"
                  v-model="selectedParameterService"
                  name="parameter_service"
                  :initialValue="option.key"
                  :label="`${option.label}`"
                  class="me-3"
                  :disabled="isDisable"
                />
              </b-col>
            </div>
          </b-row>
        </FormulateForm>

        <b-card class="border">
          <FormulateForm ref="distanceFormRef" v-model="distanceForm">
            <b-row v-show="selectedParameterService == 'DistanceAndTime'">
              <b-col sm="12">
                <p>{{ $t('maintenance.form.kmHeader') }}</p>
              </b-col>
              <b-col md="6">
                <div class="my-2">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    :validation="[['required'], ['greaterThanZero'], ['min', distanceForm.reminderDistance], ['min', 0]]"
                    :validation-messages="{
                    greaterThanZero: $t('global.validation.minPositiveOnly'),
                      min: $t('maintenance.validation.reminderDistance'),
                    }"
                    name="intervalDistance"
                  :validation-rules="{ greaterThanZero }"
                    type="number"
                    prefix
                    suffix
                    :disabled="isDisable"
                    :help="isDisable ? '' : 'required'"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.intKm') }} </label>
                        <a v-b-tooltip.topleft :title="`${$t('maintenance.tooltip.intDistance')}`">
                          <i class="icon-outline_info"></i>
                        </a>
                      </div>
                    </template>
                    <template #prefix>
                      <div class="formulate-input-prefix">
                        <span>{{ $t('maintenance.form.every') }}</span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('dashboard.object.km') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
              <b-col md="6">
                <div class="my-2">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    :validation="[['required'], ['greaterThanZero'], ['max', distanceForm.intervalDistance], ['min', 0]]"
                    :validation-messages="{
                    greaterThanZero: $t('global.validation.minPositiveOnly'),
                      max: $t('maintenance.validation.reminderDistance'),
                    }"
                    type="number"
                    name="reminderDistance"
                  :validation-rules="{ greaterThanZero }"
                    prefix
                    suffix
                    :disabled="isDisable"
                    :help="isDisable ? '' : 'required'"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.remindKm') }} </label>
                        <span
                          v-b-tooltip.topleft
                          :title="`${$t('maintenance.tooltip.kmReminder')}`"
                        >
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                    <template #prefix>
                      <div class="formulate-input-prefix">
                        <span>{{ $t('maintenance.form.every') }}</span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('dashboard.object.km') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
              <b-col md="6">
                <div class="my-3">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    :validation="[['required'], ['greaterThanZero'], ['maxInterval', distanceForm], ['min', 0]]"
                    :validation-messages="{
                    greaterThanZero: $t('global.validation.minPositiveOnly'),
                      maxInterval: $t('maintenance.validation.reminderTime'),
                    }"
                    type="number"
                    name="intervalTime"
                  :validation-rules="{ greaterThanZero }"
                    prefix
                    suffix
                    :disabled="isDisable"
                    :help="isDisable ? '' : 'required'"
                    :label="$t('maintenance.form.intTime')"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.intTime') }} </label>
                        <span v-b-tooltip.topleft :title="`${$t('maintenance.tooltip.intTime')}`">
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                    <template #prefix>
                      <div class="formulate-input-prefix">
                        <span>{{ $t('maintenance.form.every') }}</span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="multiselect w-25 period-select">
                        <multiselect
                          v-model="distanceForm.intervalPeriod"
                          :placeholder="`${$t('global.form.select')} ${$t('global.object.period')}`"
                          :options="intPeriodeOptions"
                          :class="{ 'is-invalid': $v.distanceForm.intervalPeriod.$error }"
                          label="label"
                          :disabled="isDisable"
                        />
                        <div
                          v-if="$v.distanceForm.intervalPeriod.$error"
                          class="invalid-feedback d-block period-select__error"
                        >
                          {{ $t('maintenance.validation.periodRequired') }}
                        </div>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
              <b-col md="6">
                <div class="my-3">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    :validation="[['required'], ['greaterThanZero'], ['maxInterval', distanceForm], ['min', 0]]"
                    :validation-messages="{
                    greaterThanZero: $t('global.validation.minPositiveOnly'),
                      maxInterval: $t('maintenance.validation.reminderTime'),
                    }"
                    type="number"
                    name="reminderTime"
                  :validation-rules="{ greaterThanZero }"
                    prefix
                    suffix
                    :disabled="isDisable"
                    :help="isDisable ? '' : 'required'"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.remindTime') }} </label>
                        <span
                          v-b-tooltip.topleft
                          :title="`${$t('maintenance.tooltip.remindTime')}`"
                        >
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                    <template #prefix>
                      <div class="formulate-input-prefix">
                        <span>{{ $t('maintenance.form.before') }}</span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="multiselect w-25 period-select">
                        <multiselect
                          v-model="distanceForm.reminderPeriod"
                          :placeholder="`${$t('global.form.select')} ${$t('global.object.period')}`"
                          :options="reminderPeriodOptions"
                          :class="{ 'is-invalid': $v.distanceForm.reminderPeriod.$error }"
                          label="label"
                          :disabled="isDisable"
                        />
                        <div
                          v-if="$v.distanceForm.reminderPeriod.$error"
                          class="invalid-feedback d-block period-select__error"
                        >
                          {{ $t('maintenance.validation.periodRequired') }}
                        </div>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
              <b-col md="12">
                <div class="form-check-left form-switch">
                  <input
                    id="moreinfo-history"
                    v-model="showHideDetail"
                    class="form-check-input me-2"
                    type="checkbox"
                    :disabled="isDisable"
                  />
                  <span class="mc-span__label" for="moreinfo-history">
                    {{ $t('maintenance.form.setNextService') }}
                  </span>
                </div>
              </b-col>
              <b-col v-if="showHideDetail" md="6">
                <div class="mt-3">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    validation="min:0"
                    name="startServiceAtDistance"
                    type="number"
                    prefix
                    suffix
                    :disabled="isDisable"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.odoNextService') }} </label>
                        <span v-b-tooltip.topleft :title="$t('maintenance.tooltip.kmManual')">
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                    <template #prefix>
                      <div class="formulate-input-prefix">
                        <span>{{ $t('maintenance.form.every') }}</span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('dashboard.object.km') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
              <b-col v-if="showHideDetail" md="6">
                <div class="mt-3">
                  <FormulateInput
                    type="date-picker"
                    validation-behavior="submit"
                    error-behavior="submit"
                    format="DD MMM YYYY"
                    value-type="YYYY-MM-DD"
                    :placeholder="
                      distanceForm.startServiceAtTime
                        ? momentDateShortMonth(distanceForm.startServiceAtTime)
                        : `${$t('global.field.date')}`
                    "
                    name="startServiceAtTime"
                    :confirm="true"
                    :confirm-text="$t('global.action.apply')"
                    :validation="[['dateGteNow']]"
                    :validation-messages="{
                      dateGteNow: $t('maintenance.validation.dateGteNow'),
                    }"
                    :disabled="isDisable"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.dateNextService') }} </label>
                        <span v-b-tooltip.topleft :title="$t('maintenance.tooltip.timeManual')">
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
            </b-row>
          </FormulateForm>

          <!-- hourmeter -->
          <FormulateForm ref="hourmeterFormRef" v-model="hourmeterForm">
            <b-row v-show="selectedParameterService == 'Hourmeter'">
              <b-col sm="12">
                <p>{{ $t('maintenance.form.hourmeterHeader') }}</p>
              </b-col>
              <b-col md="6">
                <div class="my-2">
                  <FormulateInput
                    id="intervalHourmeter"
                    validation-behavior="submit"
                    error-behavior="submit"
                    :validation="[
                      ['required'], ['greaterThanZero'],
                      ['min', hourmeterForm.reminderHourmeter],
                      ['min', 0],
                    ]"
                    :validation-messages="{
                    greaterThanZero: $t('global.validation.minPositiveOnly'),
                      min: $t('maintenance.validation.reminderHourmeter'),
                    }"
                    name="intervalHourmeter"
                  :validation-rules="{ greaterThanZero }"
                    type="number"
                    prefix
                    suffix
                    :disabled="isDisable"
                    :help="isDisable ? '' : 'required'"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.hourmeterInterval') }} </label>
                        <a v-b-tooltip.topleft :title="$t('maintenance.tooltip.intHourmeter')">
                          <i class="icon-outline_info"></i>
                        </a>
                      </div>
                    </template>
                    <template #prefix>
                      <div class="formulate-input-prefix">
                        <span>{{ $t('maintenance.form.every') }}</span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('global.time.hours') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
              <b-col md="6">
                <div class="my-2">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    :validation="[
                      ['required'], ['greaterThanZero'],
                      ['max', hourmeterForm.intervalHourmeter],
                      ['min', 0],
                    ]"
                    :validation-messages="{
                    greaterThanZero: $t('global.validation.minPositiveOnly'),
                      max: $t('maintenance.validation.reminderHourmeter'),
                    }"
                    type="number"
                    name="reminderHourmeter"
                  :validation-rules="{ greaterThanZero }"
                    prefix
                    suffix
                    :disabled="isDisable"
                    :help="isDisable ? '' : 'required'"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.hourmeterIntervalReminder') }} </label>
                        <span
                          v-b-tooltip.topleft
                          :title="$t('maintenance.tooltip.hourmeterReminder')"
                        >
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                    <template #prefix>
                      <div class="formulate-input-prefix">
                        <span>{{ $t('maintenance.form.every') }}</span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('global.time.hours') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
              <b-col md="12">
                <div class="form-check-left form-switch">
                  <input
                    id="moreinfo-history"
                    v-model="showHideDetailHourmeter"
                    class="form-check-input me-2"
                    type="checkbox"
                    :disabled="isDisable"
                  />
                  <span class="" for="moreinfo-history">
                    {{ $t('maintenance.form.setNextService') }}
                  </span>
                </div>
              </b-col>
              <b-col v-if="showHideDetailHourmeter" md="6">
                <div class="mt-3">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    validation="min:0"
                    name="startServiceAtHourmeter"
                    type="number"
                    prefix
                    suffix
                    :disabled="isDisable"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.hourmeterDuration') }} </label>
                        <span
                          v-b-tooltip.topleft
                          :title="$t('maintenance.tooltip.hourmeterManual')"
                        >
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('global.time.hours') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
            </b-row>
          </FormulateForm>

          <!-- refrigerator -->
          <FormulateForm ref="refrigeratorFormRef" v-model="refrigeratorForm">
            <b-row v-show="selectedParameterService == 'Refrigerator'">
              <b-col sm="12">
                <p>{{ $t('maintenance.form.refrigeratorHeader') }}</p>
              </b-col>
              <b-col md="6">
                <div class="my-2">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    :validation="[
                      ['required'], ['greaterThanZero'],
                      ['min', refrigeratorForm.reminderRefrigerator],
                      ['min', 0],
                    ]"
                    :validation-messages="{
                    greaterThanZero: $t('global.validation.minPositiveOnly'),
                      min: $t('maintenance.validation.reminderRefrigerator'),
                    }"
                    name="intervalRefrigerator"
                  :validation-rules="{ greaterThanZero }"
                    type="number"
                    prefix
                    suffix
                    :disabled="isDisable"
                    :help="isDisable ? '' : 'required'"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.refrigeratorInterval') }} </label>
                        <span
                          v-b-tooltip.topleft
                          :title="$t('maintenance.tooltip.intRefrigerator')"
                        >
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                    <template #prefix>
                      <div class="formulate-input-prefix">
                        <span>{{ $t('maintenance.form.every') }}</span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('global.time.hours') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
              <b-col md="6">
                <div class="my-2">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    :validation="[
                      ['required'], ['greaterThanZero'],
                      ['max', refrigeratorForm.intervalRefrigerator],
                      ['min', 0],
                    ]"
                    :validation-messages="{
                    greaterThanZero: $t('global.validation.minPositiveOnly'),
                      max: $t('maintenance.validation.reminderRefrigerator'),
                    }"
                    name="reminderRefrigerator"
                  :validation-rules="{ greaterThanZero }"
                    type="number"
                    prefix
                    suffix
                    :disabled="isDisable"
                    :help="isDisable ? '' : 'required'"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.refrigeratorIntervalReminder') }} </label>
                        <span
                          v-b-tooltip.topleft
                          :title="$t('maintenance.tooltip.refrigeratorReminder')"
                        >
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                    <template #prefix>
                      <div class="formulate-input-prefix">
                        <span>{{ $t('maintenance.form.every') }}</span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('global.time.hours') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>

              <b-col md="12">
                <div class="form-check-left form-switch">
                  <input
                    id="moreinfo-refrigerator"
                    v-model="showHideDetailRefrigerator"
                    class="form-check-input me-2"
                    type="checkbox"
                    :disabled="isDisable"
                  />
                  <span for="moreinfo-refrigerator">
                    {{ $t('maintenance.form.setNextService') }}
                  </span>
                </div>
              </b-col>
              <b-col v-if="showHideDetailRefrigerator" md="6">
                <div class="mt-3">
                  <FormulateInput
                    validation-behavior="submit"
                    error-behavior="submit"
                    validation="min:0"
                    name="startServiceAtRefrigerator"
                    type="number"
                    prefix
                    suffix
                    :disabled="isDisable"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between align-items-center">
                        <label>{{ $t('maintenance.form.refrigeratorDuration') }} </label>
                        <span
                          v-b-tooltip.topleft
                          :title="$t('maintenance.tooltip.refrigeratorManual')"
                        >
                          <i class="icon-outline_info"></i>
                        </span>
                      </div>
                    </template>
                    <template #suffix>
                      <div class="formulate-input-suffix">
                        <span>{{ $t('global.time.hours') }}</span>
                      </div>
                    </template>
                  </FormulateInput>
                </div>
              </b-col>
            </b-row>
          </FormulateForm>
        </b-card>

        <b-row>
          <b-col 
            v-if="
              (selectedParameterService ==='DistanceAndTime' && distanceForm.startServiceAtDistance) 
              || (selectedParameterService ==='Hourmeter' && hourmeterForm.startServiceAtHourmeter) 
              || (selectedParameterService ==='Refrigerator' && refrigeratorForm.startServiceAtRefrigerator)"
            md="12"
          >
            <b-alert show variant="primary" class="mc-alert mt-3">
              <i class="mc-fa icon-fill_info"></i>
              
              <span v-if="selectedParameterService == 'DistanceAndTime'" class="mc-alert__text">
                {{ $t('maintenance.notif.intKm') }}
                <strong v-if="distanceForm.startServiceAtDistance && isDisable">
                  {{ `${thousandFormat(distanceForm.startServiceAtDistance)} Km ` }}
                  <template v-if="serviceDate">
                    & {{ $t('global.field.date') }}
                    {{ momentDateShortMonth(distanceForm.startServiceAtTime) }}
                  </template>
                </strong>
                <strong v-else>
                  {{ `${thousandFormat(serviceDistance)} Km ` }}
                  <template v-if="serviceDate">
                    & {{ $t('global.field.date') }}
                    {{ momentDateShortMonth(serviceDate) }}
                  </template>
                </strong>
              </span>

              <span v-else-if="selectedParameterService == 'Hourmeter'" class="mc-alert__text">
                {{ $t('maintenance.notif.intHourmeter') }}
                <strong v-if="hourmeterForm.startServiceAtHourmeter && isDisable">
                  {{
                    `${
                      isNaN(hourmeterForm.startServiceAtHourmeter)
                        ? 0
                        : thousandFormat(hourmeterForm.startServiceAtHourmeter)
                    } ${$t('global.time.hours')}`
                  }}
                </strong>
                <strong v-else>
                  {{
                    `${isNaN(serviceHourmeter) ? 0 : thousandFormat(serviceHourmeter)} ${$t(
                      'global.time.hours'
                    )}`
                  }}
                </strong>
              </span>
              
              <span v-else-if="selectedParameterService == 'Refrigerator'" class="mc-alert__text">
                {{ $t('maintenance.notif.intRefrigerator') }}
                <strong v-if="refrigeratorForm.startServiceAtRefrigerator && isDisable">
                  {{
                    `${
                      isNaN(refrigeratorForm.startServiceAtRefrigerator)
                        ? 0
                        : thousandFormat(refrigeratorForm.startServiceAtRefrigerator)
                    } ${$t('global.time.hours')}`
                  }}
                </strong>
                <strong v-else>
                  {{
                    `${isNaN(serviceRefrigerator) ? 0 : thousandFormat(serviceRefrigerator)} ${$t(
                      'global.time.hours'
                    )}`
                  }}
                </strong>
              </span>

              <span v-if="methodScheduler === 'Floating'">
                {{ $t('maintenance.text.floatingRule') }}
              </span>
              <span v-else-if="methodScheduler === 'Fixed'">
                {{ $t('maintenance.text.fixedRule') }}
              </span>
            </b-alert>
          </b-col>
        </b-row>
      </b-card>
    </div>

    <b-modal
      id="mc-modal-change-to-service"
      ref="mc-modal-change-to-service"
      class="mc-modal"
      :title="$t('maintenance.changeToService')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p class="mc-general__label">
          {{
            oldData.forceInProgress
              ? $t('maintenance.confirmToNormal')
              : $t('maintenance.confirmToService')
          }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton
            variant="primary"
            :text="$t('global.action.continue')"
            @click="changeToService"
          />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper } from '@/mixin/helper'
import Multiselect from 'vue-multiselect'
import McButton from '@/components/McButton'
import McRadioButton from '@/components/McRadioButton'
import { GET_VEHICLE, GET_VEHICLE_ID, GET_VEHICLES_PAGINATED } from '@/services/api/master/fleet'
import handleApiErrorMessage from '@/helpers/handleErrorMessage/handleApiErrorMessage'
import {
  CREATE_SERVICE_SCHEDULE,
  UPDATE_SERVICE_SCHEDULE,
  GET_SERVICE_SCHEDULE,
  GET_SERVICE_SCHEDULES,
} from '@/services/api/maintenance/serviceSchedules'
import { SERVICES } from '@/services/api/setting/maintenance/setting/services'
import { GET_VEHICLE_STATUS_BY_ID } from '@/services/api/fleet'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    Multiselect,
    McRadioButton,
    McButton,
  },
  mixins: [Helper, MixPanel],
  apollo: {
    vehicles: {
      query: GET_VEHICLE,
      variables() {
        return {
          filter: {
            orderBy: JSON.stringify({ licensePlate: 'asc' }),
            status: ['R', 'I'],
          },
        }
      },
      skip() {
        return this.$route.params.vehicleId === 'undefined'
      },
    },
    serviceSchedule: {
      query: GET_SERVICE_SCHEDULE,
      fetchPolicy: 'no-cache',
      manual: true,
      variables() {
        return {
          filter: {
            serviceId: Number(this.$route.params.serviceId),
            vehicleId: Number(this.$route.params.vehicleId),
            type: this.$route.params.serviceType
              ? this.$route.params.serviceType
              : 'DistanceAndTime',
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.isUpdate = true
          this.loaded = false

          const serviceSchedule = data.serviceSchedule.serviceSchedule

          if (serviceSchedule.ssgroup) {
            this.isDisable = true
            this.ssgroup.name = serviceSchedule.ssgroup.name
            const ssgroupData = serviceSchedule.service2Ssgroup
            if (ssgroupData) {
              serviceSchedule.intervalDistance = ssgroupData.intervalDistance
              serviceSchedule.reminderDistance = ssgroupData.reminderDistance
              serviceSchedule.intervalTime = ssgroupData.intervalTime
              serviceSchedule.intervalPeriod = ssgroupData.intervalPeriod
              serviceSchedule.reminderTime = ssgroupData.reminderTime
              serviceSchedule.reminderPeriod = ssgroupData.reminderPeriod
              serviceSchedule.intervalHourmeter = ssgroupData.intervalHourmeter
              serviceSchedule.reminderHourmeter = ssgroupData.reminderHourmeter
              serviceSchedule.intervalRefrigerator = ssgroupData.intervalRefrigerator
              serviceSchedule.reminderRefrigerator = ssgroupData.reminderRefrigerator
              serviceSchedule.method = ssgroupData.method
              serviceSchedule.type = ssgroupData.type
            }
          }

          let intervalPeriod = {
            key: 'day',
            label: this.$t('global.time.days'),
          }

          switch (serviceSchedule.intervalPeriod) {
            case 'w':
              intervalPeriod = {
                key: 'week',
                label: this.$t('global.time.week'),
              }
              break
            case 'm':
              intervalPeriod = {
                key: 'month',
                label: this.$t('global.time.month'),
              }
              break
            case 'y':
              intervalPeriod = {
                key: 'year',
                label: this.$t('global.time.year'),
              }
              break

            default:
              break
          }

          let reminderPeriod = {
            key: 'day',
            label: this.$t('global.time.days'),
          }
          switch (serviceSchedule.reminderPeriod) {
            case 'w':
              reminderPeriod = {
                key: 'week',
                label: this.$t('global.time.week'),
              }
              break
            case 'm':
              reminderPeriod = {
                key: 'month',
                label: this.$t('global.time.month'),
              }
              break
            case 'y':
              reminderPeriod = {
                key: 'year',
                label: this.$t('global.time.year'),
              }
              break

            default:
              break
          }

          this.servicesDatas = serviceSchedule.service
          this.compServiceSelect = serviceSchedule.service.name
          this.categoryService = serviceSchedule.service.serviceCategory.name
          this.serviceAction = serviceSchedule.service.serviceAction.action
          this.methodScheduler = serviceSchedule.method

          this.distanceForm = {
            intervalDistance: serviceSchedule.intervalDistance,
            intervalTime: serviceSchedule.intervalTime,
            reminderDistance: serviceSchedule.reminderDistance,
            reminderTime: serviceSchedule.reminderTime,
            startServiceAtDistance: serviceSchedule.startServiceAtDistance
              ? Math.floor(serviceSchedule.startServiceAtDistance)
              : null,
            startServiceAtTime: new Date(serviceSchedule.startServiceAtTime),
            intervalPeriod: intervalPeriod,
            reminderPeriod: reminderPeriod,
          }

          this.hourmeterForm = {
            intervalHourmeter: serviceSchedule.intervalHourmeter
              ? Math.floor(serviceSchedule.intervalHourmeter / 60)
              : null,
            reminderHourmeter: serviceSchedule.reminderHourmeter
              ? Math.floor(serviceSchedule.reminderHourmeter / 60)
              : null,
            startServiceAtHourmeter: serviceSchedule.startServiceAtHourmeter
              ? Math.floor(serviceSchedule.startServiceAtHourmeter / 60)
              : null,
          }

          this.refrigeratorForm = {
            intervalRefrigerator: serviceSchedule.intervalRefrigerator
              ? Math.floor(serviceSchedule.intervalRefrigerator / 60)
              : null,
            reminderRefrigerator: serviceSchedule.reminderRefrigerator
              ? Math.floor(serviceSchedule.reminderRefrigerator / 60)
              : null,
            startServiceAtRefrigerator: serviceSchedule.startServiceAtRefrigerator
              ? Math.floor(serviceSchedule.startServiceAtRefrigerator / 60)
              : null,
          }

          this.showHideDetail = this.distanceForm.startServiceAtDistance
          this.showHideDetailHourmeter = this.hourmeterForm.startServiceAtHourmeter
          this.showHideDetailRefrigerator = this.refrigeratorForm.startServiceAtRefrigerator

          this.oldData = {
            forceInProgress: serviceSchedule.forceInProgress,
            type: serviceSchedule.type,
            ...this.form,
            ...this.distanceForm,
            ...this.hourmeterForm,
            ...this.refrigeratorForm,
            method: serviceSchedule.method,
          }
          this.form.serviceId = serviceSchedule.service.serviceId
          setTimeout(() => {
            this.loaded = true
          }, 500)
        }
      },
      skip() {
        return !this.$route.params.serviceId
      },
    },
    vehicle: {
      query: GET_VEHICLE_ID,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            id: this.$route.params.vehicleId
              ? Number(this.$route.params.vehicleId)
              : Number(this.vehicleSelected.id),
          },
        }
      },
      result({ data, loading }) {
        if (!loading && data.vehicle.isSuccessful) {
          this.vehicleSelected = data.vehicle.vehicle
          this.form.vehicleId = data.vehicle.vehicle.id
          this.isLoading = false
        }
      },
      skip() {
        return !this.$route.params.vehicleId
      },
    },
    vehicleStatus: {
      query: GET_VEHICLE_STATUS_BY_ID,
      fetchPolicy: 'no-cache',
      skip() {
        return !this.$route.params.vehicleId
      },
      variables() {
        return {
          filter: {
            vehicleId: Number(this.$route.params.vehicleId),
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        if (data) this.vehicleStatus = data.vehicleStatus.vehicleStatus
      },
    },
    services: {
      query: SERVICES,
      fetchPolicy: 'no-cache',
      result({ data, loading }) {
        if (loading) return
        this.componentServices = data.services.services.data
      },
    },

    serviceSchedules: {
      query: GET_SERVICE_SCHEDULES,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            vehicleId: Number(this.$route.params.vehicleId),
            vehicleStatus: ['I', 'R'],
            pagination: {
              page: 1,
              show: 9999,
            },
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        if (data) {
          const services = data.serviceSchedules.serviceSchedules.data.serviceSchedules
          this.$store.commit('maintenance/SET_SERVICES', services)
        }
      },
    },
  },

  validations: {
    distanceForm: {
      intervalPeriod: { required },
      reminderPeriod: { required },
    },
  },

  data() {
    return {
      compServiceSelect: null,
      compServiceOptions: [],
      conflict: [],
      licensePlateSelect: null,
      vehicleList: [],
      fullObject: {},
      vehicles: {
        vehicles: [],
      },
      componentServices: [],
      vehicleStatus: {},
      showHideDetail: false,
      showHideDetailHourmeter: false,
      showHideDetailRefrigerator: false,
      rendered: true,
      vehicleSelected: {},
      serviceSelected: {},
      servicesDatas: [],
      categoryService: null,
      serviceAction: null,
      form: {
        vehicleId: null,
        serviceId: null,
      },
      distanceForm: {
        intervalDistance: null,
        intervalTime: null,
        reminderDistance: null,
        reminderTime: null,
        startServiceAtDistance: null,
        startServiceAtTime: null,
        intervalPeriod: { key: 'day', label: this.$t('global.time.days') },
        reminderPeriod: { key: 'day', label: this.$t('global.time.days') },
      },
      hourmeterForm: {
        intervalHourmeter: null,
        reminderHourmeter: null,
        startServiceAtHourmeter: null,
      },
      refrigeratorForm: {
        intervalRefrigerator: null,
        reminderRefrigerator: null,
        startServiceAtRefrigerator: null,
      },
      oldData: {},
      isUpdate: false,
      showConflict: false,
      isDisable: false,
      ssgroup: {
        name: '',
      },

      filterId: null,
      selectedParameterService: this.$route.params.serviceType
        ? this.$route.params.serviceType
        : 'DistanceAndTime',
      isLoading: true,
      methodScheduler: 'Floating',
      loaded: false,
    }
  },

  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGMAndMO() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data.features?.maintenance_order
      )
    },
    title() {
      return this.isUpdate
        ? this.$t('maintenance.detailSchedule.title')
        : this.$t('maintenance.menu.titleAdd.schedule')
    },

    parameterServiceOptions() {
      return [
        { key: 'DistanceAndTime', label: this.$t('maintenance.form.kmTime') },
        { key: 'Hourmeter', label: this.$t('maintenance.form.hourmeter') },
        this.isMGMAndMO && { key: 'Refrigerator', label: this.$t('maintenance.form.refrigerator') },
      ].filter(Boolean)
    },

    methodSchedulerOptions() {
      return [
        {
          key: 'Floating',
          label: this.$t('maintenance.floating'),
          description: this.$t('maintenance.form.floatingDesc'),
          tooltip: this.$t('maintenance.tooltip.floating'),
        },
        {
          key: 'Fixed',
          label: this.$t('maintenance.fixed'),
          description: this.$t('maintenance.form.fixedDesc'),
          tooltip: this.$t('maintenance.tooltip.fixed'),
        },
      ]
    },

    items() {
      return [
        {
          text: this.$t('maintenance.title'),
          to: { name: 'Service' },
        },
        {
          text: this.$t('maintenance.frontPage.title.serviceSchedule'),
          to: { name: 'List Service Schedule' },
        },
        {
          text: this.isUpdate
            ? this.$t('maintenance.detailSchedule.title')
            : this.$t('maintenance.menu.titleAdd.schedule'),
        },
      ]
    },

    intPeriodeOptions() {
      return [
        { key: 'day', label: this.$t('global.time.days') },
        { key: 'week', label: this.$t('global.time.week') },
        { key: 'month', label: this.$t('global.time.month') },
      ]
    },
    reminderPeriodOptions() {
      return [
        { key: 'day', label: this.$t('global.time.days') },
        { key: 'week', label: this.$t('global.time.week') },
      ]
    },

    serviceDistance() {
      let distance = 0

      if (!this.distanceForm.intervalDistance && !this.distanceForm.startServiceAtDistance) {
        return null
      } else {
        if (this.methodScheduler === 'Floating') {
          distance = !this.showHideDetail
            ? Number(this.distanceForm.intervalDistance) + Number(this.vehicleSelected?.odometer)
            : this.distanceForm.startServiceAtDistance
        } else if (this.methodScheduler === 'Fixed') {
          const division =
            Number(parseFloat(this.vehicleSelected?.odometer).toFixed(2)) /
            Number(this.distanceForm.intervalDistance)

          let result =
            (division % 1 === 0 ? division + 1 : Math.ceil(division)) *
            Number(this.distanceForm.intervalDistance)

          distance = !this.showHideDetail ? result : this.distanceForm.startServiceAtDistance
        }
      }

      return distance
    },

    serviceHourmeter() {
      let distance = 0
      if (!this.hourmeterForm.intervalHourmeter && !this.hourmeterForm.startServiceAtHourmeter) {
        return null
      } else {
        if (this.methodScheduler === 'Floating') {
          distance = !this.showHideDetailHourmeter
            ? Number(this.hourmeterForm.intervalHourmeter) +
              Math.floor(this.vehicleSelected?.hourmeter / 60)
            : this.hourmeterForm.startServiceAtHourmeter
        } else if (this.methodScheduler === 'Fixed') {
          const division =
            Math.floor(this.vehicleSelected?.hourmeter / 60) /
            Number(this.hourmeterForm.intervalHourmeter)

          let result =
            (division % 1 === 0 ? division + 1 : Math.ceil(division)) *
            Number(this.hourmeterForm.intervalHourmeter)

          distance = !this.showHideDetail ? result : this.hourmeterForm.startServiceAtHourmeter
        }
      }

      return distance
    },

    serviceRefrigerator() {
      if (
        !this.refrigeratorForm.intervalRefrigerator &&
        !this.refrigeratorForm.startServiceAtRefrigerator
      ) {
        return null
      }

      return this.showHideDetailRefrigerator
        ? this.refrigeratorForm.startServiceAtRefrigerator
        : Number(this.refrigeratorForm.intervalRefrigerator)
    },

    serviceDate() {
      if (!this.distanceForm.intervalTime && !this.distanceForm.startServiceAtTime) {
        return null
      }
      let date = null
      if (this.methodScheduler == 'Floating') {
        date = this.momentAdd(
          new Date(),
          this.distanceForm.intervalTime,
          this.distanceForm.intervalPeriod.key
        )
      } else {
        const today = new Date()
        const startDate = new Date(today.getFullYear(), 0, 1)

        const addOn =
          Number(this.distanceForm.intervalTime) *
          (this.distanceForm.intervalPeriod.key === 'day' ? 86400000 : 604800000)

        const diffDate = Math.abs(startDate.valueOf() - today.valueOf())
        const division = diffDate / Number(addOn)
        date = new Date(
          startDate.valueOf() + Number(addOn) * (division % 1 === 0 ? 1 : Math.ceil(division))
        )

        if (this.distanceForm.intervalPeriod.key == 'month') {
          let multiplier = today.getMonth() / this.distanceForm.intervalTime
          if (multiplier % 1 === 0) multiplier += 1
          else multiplier = Math.ceil(multiplier)
          date = new Date(
            startDate.setMonth(startDate.getMonth() + this.distanceForm.intervalTime * multiplier)
          )
        }
      }

      return !this.showHideDetail ? date : this.distanceForm.startServiceAtTime
    },

    lastPacket() {
      return this.vehicleStatus.lastPacket
    },

    queryCompService() {
      return {
        get: async (opts) => {
          let result = {}
          const self = this

          if (self.$store.state.maintenance.services) {
            const filterService = self.$store.state.maintenance.services.filter((item) => {
              return item.vehicleId == this.form.vehicleId
            })
            self.filterId = filterService.map((item) => item.serviceId)
          }

          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            orderBy: opts.sorts,
            search: opts.search,
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: SERVICES,
            variables: {
              filter: {
                ...filter,
                excludeServiceId: self.filterId ? self.filterId : null,
              },
            },
          })

          const data = res.data.services.services

          result = {
            metadata: {
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          return result
        },
      }
    },

    fieldCompService() {
      return [
        {
          key: 'name',
          label: this.$t('maintenance.field.compName'),
          sortable: true,
          formatter: (val, key, item) => {
            return `${val} (${item.serviceAction.action})`
          },
        },
        {
          key: 'serviceCategory.name',
          label: this.$t('global.field.category'),
          sortable: true,
        },
      ]
    },
    resourcefulQuery() {
      return {
        licensePlate: async (opts) => {
          let result = {}

          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            parentOnly: true,
            orderBy: opts?.sorts,
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_VEHICLES_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filter,
            },
          })

          const data = res.data.vehiclesPaginated.vehiclesPaginated

          result = {
            metadata: {
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          return result
        },
      }
    },
  },

  watch: {
    vehicleSelected(val) {
      this.form.vehicleId = val ? val.id : null
    },
    compServiceSelect: {
      async handler(value) {
        if (!this.isUpdate) {
          const compService = this.componentServices.find((item) => {
            return item.serviceId == value
          })

          this.servicesDatas = compService
          this.form.serviceId = compService?.serviceId

          this.categoryService = this.servicesDatas?.serviceCategory?.name
          this.serviceAction = this.servicesDatas?.serviceAction?.action
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.$route.params.serviceId) this.isUpdate = true

    this.$apollo.queries.serviceSchedule.refetch()
    this.$apollo.queries.serviceSchedules.refetch()
  },

  methods: {
    greaterThanZero({ value }) {
      return Number(value) > 0
    },

    componentLabel({ name, serviceAction }) {
      return `${name} (${serviceAction.action})`
    },

    compServiceModal() {
      this.$bvModal.show('modal-comp-service')
    },

    refreshVehicle() {
      this.$apollo.queries.vehicle.refetch({
        filter: {
          id: this.$route.params.vehicleId
            ? Number(this.$route.params.vehicleId)
            : Number(this.vehicleSelected.id),
        },
      })

      this.getOdometerUpdate()
    },

    async changeToService() {
      const input = {
        newData: { forceInProgress: !this.oldData.forceInProgress },
        oldData: { forceInProgress: this.oldData.forceInProgress },
        vehicleId: Number(this.$route.params.vehicleId),
        serviceId: Number(this.$route.params.serviceId),
        type: this.selectedParameterService,
      }
      await this.$apollo
        .mutate({
          mutation: UPDATE_SERVICE_SCHEDULE,
          variables: {
            input: input,
          },
        })
        .then((response) => {
          if (response.data.updateServiceSchedule.isSuccessful) {
            this.$mcToast.success(this.$t('global.alert.text.changed'))

            if (this.oldData.forceInProgress)
              this.track(`Change Status "Selesai Servis" on Service Scheduler`)
            else this.track(`Change Status "Sedang Servis" on Service Scheduler`)

            setTimeout(() => {
              this.$router.push({
                name: 'Detail Service Schedule',
                params: { vehicleId: this.$route.params.vehicleId },
              })
            }, 300)
          } else this.$mcToast.error(response.data.updateServiceSchedule.message)
        })
    },

    async submitData() {
      const parameterForm =
        this.selectedParameterService === 'Hourmeter'
          ? this.$refs.hourmeterFormRef
          : this.selectedParameterService === 'Refrigerator'
          ? this.$refs.refrigeratorFormRef
          : this.$refs.distanceFormRef
      const forms = [this.$refs.formRef, parameterForm].filter(Boolean)

      forms.forEach((form) => form.showErrors())
      if (this.selectedParameterService === 'DistanceAndTime') this.$v.distanceForm.$touch()

      const hasInvalidPeriod =
        this.selectedParameterService === 'DistanceAndTime' && this.$v.distanceForm.$invalid
      if (forms.some((form) => form.hasErrors) || hasInvalidPeriod) return false

      const { form, distanceForm, hourmeterForm, refrigeratorForm } = this

      let q = {}
      let vehicleId = 0
      let input = {
        type: this.selectedParameterService,
      }
      let oldInput = {}

      if (this.selectedParameterService == 'DistanceAndTime') {
        input = {
          ...input,
          intervalDistance: Number(distanceForm.intervalDistance),
          intervalTime: Number(distanceForm.intervalTime),
          intervalPeriod: distanceForm.intervalPeriod.key,
          reminderDistance: Number(distanceForm.reminderDistance),
          reminderTime: Number(distanceForm.reminderTime),
          reminderPeriod: distanceForm.reminderPeriod.key,
          method: this.methodScheduler,
          startServiceAtDistance: this.showHideDetail
            ? Number(distanceForm.startServiceAtDistance)
            : undefined,
          startServiceAtTime: this.showHideDetail
            ? this.momentToISOString(distanceForm.startServiceAtTime)
            : undefined,
          intervalHourmeter: null,
          reminderHourmeter: null,
          startServiceAtHourmeter: null,
          intervalRefrigerator: null,
          reminderRefrigerator: null,
          startServiceAtRefrigerator: null,
        }
        if (this.isUpdate) {
          oldInput = {
            intervalDistance: null,
            intervalTime: null,
            intervalPeriod: null,
            reminderDistance: null,
            reminderTime: null,
            reminderPeriod: null,
            startServiceAtDistance: null,
            startServiceAtTime: null,
            method: this.oldData.method,
            intervalHourmeter: this.oldData.intervalHourmeter,
            reminderHourmeter: this.oldData.reminderHourmeter,
            startServiceAtHourmeter: this.oldData.startServiceAtHourmeter,
            type: this.oldData.type,
            intervalRefrigerator: this.oldData.intervalRefrigerator,
            reminderRefrigerator: this.oldData.reminderRefrigerator,
            startServiceAtRefrigerator: this.oldData.startServiceAtRefrigerator,
          }
          if (this.selectedParameterService == this.oldData.type) {
            input = {
              intervalDistance: Number(distanceForm.intervalDistance),
              intervalTime: Number(distanceForm.intervalTime),
              intervalPeriod: distanceForm.intervalPeriod.key,
              reminderDistance: Number(distanceForm.reminderDistance),
              reminderTime: Number(distanceForm.reminderTime),
              reminderPeriod: distanceForm.reminderPeriod.key,
              startServiceAtDistance: this.showHideDetail
                ? Number(distanceForm.startServiceAtDistance)
                : undefined,
              startServiceAtTime: this.showHideDetail
                ? this.momentToISOString(distanceForm.startServiceAtTime)
                : undefined,
              method: this.methodScheduler,
            }
            oldInput = {
              intervalDistance: this.oldData.intervalDistance,
              intervalTime: this.oldData.intervalTime,
              intervalPeriod: this.oldData.intervalPeriod.key ?? distanceForm.intervalPeriod.key,
              reminderDistance: this.oldData.reminderDistance,
              reminderTime: this.oldData.reminderTime,
              reminderPeriod: this.oldData.reminderPeriod.key ?? distanceForm.reminderPeriod.key,
              startServiceAtDistance: this.showHideDetail
                ? this.oldData.startServiceAtDistance
                : undefined,
              startServiceAtTime: this.showHideDetail
                ? this.momentToISOString(this.oldData.startServiceAtTime)
                : undefined,
              method: this.oldData.method,
            }
          }
        }
      } else if (this.selectedParameterService == 'Hourmeter') {
        input = {
          ...input,
          intervalHourmeter: Number(hourmeterForm.intervalHourmeter),
          reminderHourmeter: Number(hourmeterForm.reminderHourmeter),
          startServiceAtHourmeter: this.showHideDetailHourmeter
            ? Number(hourmeterForm.startServiceAtHourmeter)
            : undefined,
          method: this.methodScheduler,
          intervalDistance: null,
          intervalTime: null,
          intervalPeriod: null,
          reminderDistance: null,
          reminderTime: null,
          reminderPeriod: null,
          startServiceAtDistance: null,
          startServiceAtTime: null,
          intervalRefrigerator: null,
          reminderRefrigerator: null,
          startServiceAtRefrigerator: null,
        }
        if (this.isUpdate) {
          oldInput = {
            intervalDistance: this.oldData.intervalDistance,
            intervalTime: this.oldData.intervalTime,
            intervalPeriod: this.oldData.intervalPeriod.key ?? hourmeterForm.intervalPeriod.key,
            reminderDistance: this.oldData.reminderDistance,
            reminderTime: this.oldData.reminderTime,
            reminderPeriod: this.oldData.reminderPeriod.key ?? hourmeterForm.reminderPeriod.key,
            startServiceAtDistance: this.oldData.startServiceAtDistance,
            startServiceAtTime: this.momentToISOString(this.oldData.startServiceAtTime),
            method: this.oldData.method,
            intervalHourmeter: null,
            reminderHourmeter: null,
            startServiceAtHourmeter: null,
            type: this.oldData.type,
            intervalRefrigerator: this.oldData.intervalRefrigerator,
            reminderRefrigerator: this.oldData.reminderRefrigerator,
            startServiceAtRefrigerator: this.oldData.startServiceAtRefrigerator,
          }
          if (this.selectedParameterService == this.oldData.type) {
            input = {
              intervalHourmeter: Number(hourmeterForm.intervalHourmeter),
              reminderHourmeter: Number(hourmeterForm.reminderHourmeter),
              startServiceAtHourmeter: this.showHideDetailHourmeter
                ? Number(hourmeterForm.startServiceAtHourmeter)
                : undefined,
              method: this.methodScheduler,
            }
            oldInput = {
              intervalHourmeter: this.oldData.intervalHourmeter,
              reminderHourmeter: this.oldData.reminderHourmeter,
              startServiceAtHourmeter: this.showHideDetailHourmeter
                ? this.oldData.startServiceAtHourmeter
                : undefined,
              method: this.oldData.method,
            }
          }
        }
      } else if (this.selectedParameterService == 'Refrigerator') {
        input = {
          ...input,
          intervalRefrigerator: Number(refrigeratorForm.intervalRefrigerator),
          reminderRefrigerator: Number(refrigeratorForm.reminderRefrigerator),
          startServiceAtRefrigerator: this.showHideDetailRefrigerator
            ? Number(refrigeratorForm.startServiceAtRefrigerator)
            : undefined,
          method: this.methodScheduler,
          intervalDistance: null,
          intervalTime: null,
          intervalPeriod: null,
          reminderDistance: null,
          reminderTime: null,
          reminderPeriod: null,
          startServiceAtDistance: null,
          startServiceAtTime: null,
          intervalHourmeter: null,
          reminderHourmeter: null,
          startServiceAtHourmeter: null,
        }
        if (this.isUpdate) {
          oldInput = {
            intervalDistance: this.oldData.intervalDistance,
            intervalTime: this.oldData.intervalTime,
            intervalPeriod:
              this.oldData.intervalPeriod?.key ?? refrigeratorForm.intervalPeriod?.key,
            reminderDistance: this.oldData.reminderDistance,
            reminderTime: this.oldData.reminderTime,
            reminderPeriod:
              this.oldData.reminderPeriod?.key ?? refrigeratorForm.reminderPeriod?.key,
            startServiceAtDistance: this.oldData.startServiceAtDistance,
            startServiceAtTime: this.momentToISOString(this.oldData.startServiceAtTime),
            intervalHourmeter: this.oldData.intervalHourmeter,
            reminderHourmeter: this.oldData.reminderHourmeter,
            startServiceAtHourmeter: this.oldData.startServiceAtHourmeter,
            intervalRefrigerator: null,
            reminderRefrigerator: null,
            startServiceAtRefrigerator: null,
            method: this.oldData.method,
            type: this.oldData.type,
          }
          if (this.selectedParameterService == this.oldData.type) {
            input = {
              intervalRefrigerator: Number(refrigeratorForm.intervalRefrigerator),
              reminderRefrigerator: Number(refrigeratorForm.reminderRefrigerator),
              startServiceAtRefrigerator: this.showHideDetailRefrigerator
                ? Number(refrigeratorForm.startServiceAtRefrigerator)
                : undefined,
              method: this.methodScheduler,
            }
            oldInput = {
              intervalRefrigerator: this.oldData.intervalRefrigerator,
              reminderRefrigerator: this.oldData.reminderRefrigerator,
              startServiceAtRefrigerator: this.showHideDetailRefrigerator
                ? this.oldData.startServiceAtRefrigerator
                : undefined,
              method: this.oldData.method,
            }
          }
        }
      }

      if (this.isUpdate) {
        q = { query: UPDATE_SERVICE_SCHEDULE, param: 'updateServiceSchedule' }
        input = {
          newData: { ...input },
          oldData: { ...oldInput },
          vehicleId: Number(this.$route.params.vehicleId),
          serviceId: Number(this.$route.params.serviceId),
          type: this.$route.params.serviceType,
        }
        vehicleId = Number(this.$route.params.vehicleId)
      } else {
        input = {
          ...input,
          serviceId: Number(form.serviceId),
          vehicleId: Number(form.vehicleId),
        }
        q = { query: CREATE_SERVICE_SCHEDULE, param: 'createServiceSchedule' }
        vehicleId = Number(form.vehicleId)
      }

      const self = this

      await this.$apollo
        .mutate({
          mutation: q.query,
          variables: {
            input: input,
          },
        })
        .then((response) => {
          if (response.data[q.param].isSuccessful) {
            self.$mcToast.success(this.$t('global.alert.text.saved'))

            if (this.$route.params.vehicleId)
              this.track('Success Add New Data on Service Scheduler Detail')
            else this.track('Success Create New Data on Service Scheduler')

            setTimeout(() => {
              self.$router.push({
                name: 'Detail Service Schedule',
                params: { vehicleId: vehicleId },
              })
            }, 300)
          } else self.$mcToast.error(handleApiErrorMessage(response.data[q.param]))
        })
    },

    async getOdometerUpdate() {
      const res = await this.$apollo.queries.vehicleStatus.refetch({
        filter: {
          vehicleId: Number(this.$route.params.vehicleId),
        },
      })

      this.vehicleStatus = res.data.vehicleStatus.vehicleStatus
      return res
    },
    handleCancel() {
      return this.$route.params.vehicleId
        ? this.$router.push({
            name: 'Detail Service Schedule',
            vehicleId: this.$route.params.vehicleId,
          })
        : this.$router.push({ name: 'List Service Schedule' })
    },

    handleSetMethod(value) {
      this.methodScheduler = value
    },
  },
}
</script>

<style lang="scss" scoped>
.period-select {
  position: relative;

  &__error {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 0.25rem;
  }
}
</style>
